import { Static, Type } from '@sinclair/typebox';
import { AddressSchema } from './professional';
import { intraEuVatNumberPattern } from './vat';
import {
  ExpressPaymentStatus,
  PaymentMethod,
  PaymentProvider,
  PaymentStatus,
  PaymentType,
  SepaStartStatus,
} from './translation_namespace/payment_namespace';

const PaymentItemSchema = Type.Object({
  label_trad: Type.Optional(Type.String()),
  desc_trad: Type.Optional(Type.String()),
  quantity: Type.Number(),
  price: Type.Number(),
  price_string: Type.String(),
  trial_days_remaining: Type.Optional(Type.Number()),
  renewal_date: Type.Optional(Type.String({ format: 'date-time' })),
  renewal_date_string: Type.Optional(Type.String({ format: 'date-time', meta: { is_short: true, with_time: false } })),
  commitment_months: Type.Optional(Type.Number()),
  commitment_months_string: Type.Optional(Type.String()),
  payment_type: Type.Enum(PaymentType),
  is_prorata: Type.Optional(Type.Boolean()),
  prorata_end_date_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'prorata_end_date', is_short: true, with_time: false } }),
  ),
  full_monthly_price_string: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'full_monthly_price' } })),
  is_downgrade: Type.Optional(Type.Boolean()),
  downgrade_effective_date_formatted: Type.Optional(
    Type.String({
      format: 'date-time',
      meta: { source_key: 'downgrade_effective_date', is_short: true, with_time: false },
    }),
  ),
  is_reclaim: Type.Optional(Type.Boolean()),
  reclaim_renewal_date_formatted: Type.Optional(
    Type.String({
      format: 'date-time',
      meta: { source_key: 'reclaim_renewal_date', is_short: true, with_time: false },
    }),
  ),
});

const commonPaymentToMakeRequestFields = {
  uuid: Type.String(),
  current_url_page: Type.String(),
};
export const PaymentToMakeRequestSchema = Type.Object({
  ...commonPaymentToMakeRequestFields,
  payment_method: Type.Enum(PaymentMethod),
});
export type PaymentToMakeRequestObject = Static<typeof PaymentToMakeRequestSchema>;

/**
 * Mandat SEPA (GoCardless) présenté au pro AUTHENTIFIÉ avant un prélèvement : libellé du moyen de
 * paiement (« SEPA •••• 12 »), banque, titulaire et fin d'IBAN. JAMAIS d'identifiant (ni mandat `MD…`,
 * ni ligne en base) : c'est le backend qui retrouve le mandat. Jamais servi par une route publique
 * (uuid porteur) : `GET app/payment/sepa/mandate/:payment_uuid` et le paiement express seulement.
 */
export const SepaMandateSummarySchema = Type.Object({
  label: Type.Optional(Type.String()),
  bank_name: Type.Optional(Type.String()),
  account_holder_name: Type.Optional(Type.String()),
  iban_last2: Type.Optional(Type.String()),
});
export type SepaMandateSummaryObject = Static<typeof SepaMandateSummarySchema>;

/**
 * Marqueurs d'URL des retours de paiement — une seule définition, posée par le front ou le backend et
 * lue par le front (règle 35). GoCardless renvoie le pro sur la page d'origine après la signature d'un
 * mandat (`redirect_uri`) et à la SORTIE du parcours sans signer (`exit_uri`) :
 *  - `PAYMENT_UUID_PARAM` : paiement à relire au retour, ajouté par le backend (carte comme SEPA) ;
 *  - `SEPA_RETURN_PARAM` (`=1`, posé par le front sur la page de paiement) et `SEPA_MANDATE_RETURN_PARAM`
 *    (`=<dernier payment_method_id connu>`, « Moyens de paiement ») : retour de signature — seul le
 *    webhook conclut, le front relit l'état pendant un temps borné ;
 *  - `SEPA_EXIT_PARAM` (`=1`, posé par le BACKEND sur `exit_uri`, sans les marqueurs de retour) : le
 *    payeur est sorti sans signer — rien à attendre, la page reste payable.
 */
export const PAYMENT_UUID_PARAM = 'payment_uuid';
export const SEPA_RETURN_PARAM = 'sepa_return';
export const SEPA_MANDATE_RETURN_PARAM = 'sepa_mandate_after';
export const SEPA_EXIT_PARAM = 'sepa_exit';

/**
 * Prélèvement SEPA depuis la page PUBLIQUE de paiement (`POST public/payment/sepa/start`, uuid porteur) :
 * signature d'un NOUVEAU mandat chez GoCardless, retour sur `current_url_page` — le prélèvement part au
 * webhook de signature. La route publique ne prélève JAMAIS un mandat déjà enregistré : c'est
 * `POST app/payment/sepa/pay-with-mandate`, réservé au pro connecté.
 */
export const PaymentSepaStartRequestSchema = Type.Object({
  ...commonPaymentToMakeRequestFields,
});
export type PaymentSepaStartRequestObject = Static<typeof PaymentSepaStartRequestSchema>;

/**
 * Issue d'un prélèvement SEPA demandé depuis la page de paiement : `REDIRECT` (signature d'un nouveau
 * mandat, route publique), `PROCESSING` (prélèvement lancé sur le mandat du pro) ou `PAID` (rien à
 * prélever, validé avec ce mandat) — ces deux derniers par la route authentifiée seulement.
 */
export const PaymentSepaStartResponseSchema = Type.Object({
  status: Type.Enum(SepaStartStatus),
  /** Page GoCardless de signature du mandat — statut `REDIRECT` uniquement. */
  redirect_url: Type.Optional(Type.String()),
});
export type PaymentSepaStartResponseObject = Static<typeof PaymentSepaStartResponseSchema>;

/**
 * « Payer avec mon mandat SEPA » (`POST app/payment/sepa/pay-with-mandate`, pro connecté, propriétaire du
 * paiement) : prélève le mandat prélevable du pro — par défaut, sinon le plus récent. Réponse :
 * `PaymentSepaStartResponseSchema` (`PROCESSING` ou `PAID`).
 */
export const PaymentSepaPayWithMandateRequestSchema = Type.Object({
  uuid: Type.String(),
});
export type PaymentSepaPayWithMandateRequestObject = Static<typeof PaymentSepaPayWithMandateRequestSchema>;

/**
 * Mandat que « Payer avec mon mandat SEPA » prélèverait pour ce paiement (`GET app/payment/sepa/mandate/:payment_uuid`,
 * pro connecté, propriétaire du paiement). Absent : paiement non prélevable (réglé, expiré, SEPA non
 * autorisé) ou aucun mandat prélevable — la page ne propose alors que la signature d'un nouveau mandat.
 */
export const PaymentSepaMandateContextResponseSchema = Type.Object({
  existing_mandate: Type.Optional(SepaMandateSummarySchema),
});
export type PaymentSepaMandateContextResponseObject = Static<typeof PaymentSepaMandateContextResponseSchema>;

export const PaymentToMakeResponseSchema = Type.Object({
  uuid: Type.String(),
  vat_percentage: Type.Number(),
  amount_all_taxes: Type.Number(),
  amount_all_taxes_string: Type.String({
    meta: { is_price: true },
  }),
  amount_tax_free: Type.Number(),
  amount_tax_free_string: Type.String({
    meta: { is_price: true },
  }),
  amount_tax: Type.Number(),
  amount_tax_string: Type.String({
    meta: { is_price: true },
  }),
  amount_to_charge: Type.Number(),
  amount_to_charge_string: Type.String({
    meta: { is_price: true, source_key: 'amount_to_charge' },
  }),
  status: Type.Enum(PaymentStatus),
  provider: Type.Enum(PaymentProvider),
  items: Type.Array(PaymentItemSchema),
  allowed_payment_methods: Type.Array(Type.Enum(PaymentMethod)),
  success_url: Type.String(),
  /**
   * Secret de la PaymentIntent Stripe du formulaire carte. VIDE quand le paiement n'est pas payable
   * par carte : prélèvement SEPA déjà lancé (`PROCESSING`, ou échoué après lancement), carte non
   * autorisée pour ce paiement — aucune PaymentIntent n'est alors créée (jamais carte + prélèvement).
   */
  client_secret: Type.String(),
  expiration_date: Type.Date({ format: 'date-time' }),

  // Code promo — le champ de saisie n'est proposé que sur un checkout d'abonnement au
  // prix plein (`promo_input_available`). Les cas prorata / reclaim / downgrade en sont
  // exclus : leur montant n'est pas le prix mensuel, une remise dessus n'aurait pas de
  // sens métier. Les `amount_*` ci-dessus reflètent DÉJÀ la remise quand un code est posé.
  promo_input_available: Type.Optional(Type.Boolean()),
  promo_code_applied: Type.Optional(Type.String()),
  promo_label: Type.Optional(Type.String()),
  promo_discount: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'promo_discount_minor' } })),
  /**
   * Même remise, exprimée **HT**. Le récapitulatif se lit ligne à ligne
   * (`Sous-total HT` − `Remise` = `Total HT`, puis TVA, puis TTC) : y insérer la remise TTC
   * (`promo_discount`) donnerait une soustraction fausse. `promo_discount` reste le montant
   * annoncé au client (« X € de remise »), qui doit lui être TTC — c'est ce qu'il paie en moins.
   */
  promo_discount_tax_free: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'promo_discount_tax_free_minor' } }),
  ),
  /** Sous-total HT au prix catalogue (avant remise). Absent si aucun code n'est posé. */
  amount_tax_free_before_discount_string: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'amount_tax_free_before_discount' } }),
  ),
  /**
   * Montant TTC de la PROCHAINE échéance. Différent du total du jour dès que la remise ne
   * couvre pas la période suivante (code non « à vie » ou expiré d'ici là) : afficher le total
   * remisé du jour promettrait alors un prix que le pro ne paiera pas.
   */
  next_invoice_amount_string: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'next_invoice_amount_minor' } }),
  ),
  promo_is_forever: Type.Optional(Type.Boolean()),
  promo_ends_at_string: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'promo_ends_at', is_short: true } }),
  ),
  /**
   * Le paiement règle-t-il un abonnement ? Même règle que la livraison d'un prélèvement SEPA : un
   * abonnement est actif dès le lancement, un achat ponctuel (crédits, domaine) seulement à
   * l'encaissement. Seule source fiable pour la page : `items[].payment_type` n'est pas persisté.
   */
  is_subscription: Type.Boolean(),
});
export type PaymentToMakeResponseObject = Static<typeof PaymentToMakeResponseSchema>;

export const PaymentDataResponseSchema = Type.Object({
  uuid: Type.String(),
  success_url: Type.String(),
  redirect_url: Type.String(),
  status: Type.Enum(PaymentStatus),
  updated_at: Type.String({ format: 'date-time' }),
  expiration_date: Type.Date({ format: 'date-time' }),
  /**
   * Clé d'erreur métier (`API_ERRORS`) quand un mandat signé sur cette page n'a lancé AUCUN prélèvement
   * (aujourd'hui `SEPA_DEBIT_IN_FLIGHT` : un prélèvement SEPA du pro était en vol). Page toujours
   * payable ; la page l'affiche au retour de GoCardless au lieu d'attendre. Absent sinon.
   */
  sepa_launch_error: Type.Optional(Type.String()),
});
export type PaymentDataResponseObject = Static<typeof PaymentDataResponseSchema>;

const PaymentMethodStripeCardSchema = Type.Object({
  brand: Type.String(),
  last4: Type.String(),
  exp_month: Type.Number(),
  exp_year: Type.Number(),
});

const PaymentMethodSepaMandateSchema = Type.Object({
  gocardless_mandate_id: Type.String(),
  scheme: Type.String(),
  status: Type.String(),
  reference: Type.Optional(Type.String()),
  iban_last2: Type.Optional(Type.String()),
  account_holder_name: Type.Optional(Type.String()),
  bank_name: Type.Optional(Type.String()),
  country: Type.Optional(Type.String()),
  /**
   * Mandat prélevable : calculé par le backend avec la règle des débits (statut GoCardless
   * utilisable). `false` = mandat inactif (annulé, expiré, rejeté…) que plus aucun prélèvement ne
   * vise — le front le signale sans jamais interpréter lui-même un statut GoCardless.
   */
  is_chargeable: Type.Boolean(),
});

export const PaymentMethodResponseSchema = Type.Object({
  payment_method_id: Type.Number(),
  type: Type.Enum(PaymentMethod),
  provider: Type.Enum(PaymentProvider),
  label: Type.Optional(Type.String()),
  is_default: Type.Boolean(),
  stripe_card: Type.Optional(PaymentMethodStripeCardSchema),
  sepa_mandate: Type.Optional(PaymentMethodSepaMandateSchema),
});
export type PaymentMethodResponseObject = Static<typeof PaymentMethodResponseSchema>;

export const PaymentMethodDefaultUpdatedSchema = Type.Object({
  old_default: Type.Optional(PaymentMethodResponseSchema),
  new_default: PaymentMethodResponseSchema,
});
export type PaymentMethodDefaultUpdatedObject = Static<typeof PaymentMethodDefaultUpdatedSchema>;

export const PaymentMethodSetupIntentResponseSchema = Type.Object({
  client_secret: Type.String(),
});
export type PaymentMethodSetupIntentResponseObject = Static<typeof PaymentMethodSetupIntentResponseSchema>;

/**
 * Ajout d'un mandat SEPA depuis « Moyens de paiement » (`POST app/payment/methods/sepa-mandate`).
 * Le moyen de paiement n'apparaît qu'une fois le mandat signé (webhook GoCardless), comme le
 * moyen par défaut — le front relit la liste au retour.
 */
export const PaymentMethodSepaMandateRequestSchema = Type.Object({
  /**
   * Page du front d'où part l'ajout : retour du parcours de signature GoCardless ; la sortie sans
   * signature y revient marquée `SEPA_EXIT_PARAM`.
   */
  current_url_page: Type.String(),
});
export type PaymentMethodSepaMandateRequestObject = Static<typeof PaymentMethodSepaMandateRequestSchema>;

export const PaymentMethodSepaMandateResponseSchema = Type.Object({
  /** Page GoCardless de signature du mandat. */
  redirect_url: Type.String(),
});
export type PaymentMethodSepaMandateResponseObject = Static<typeof PaymentMethodSepaMandateResponseSchema>;

export const RedirectGoCardlessFlowRequestSchema = Type.Object({
  session_token: Type.String(),
  success_redirect_url: Type.String(),
  prefilled_customer: Type.Optional(
    Type.Object({
      email: Type.Optional(Type.String()),
      given_name: Type.Optional(Type.String()),
      family_name: Type.Optional(Type.String()),
      address_line1: Type.Optional(Type.String()),
      city: Type.Optional(Type.String()),
      postal_code: Type.Optional(Type.String()),
      country_code: Type.Optional(Type.String()),
    }),
  ),
});
export type RedirectGoCardlessFlowRequestObject = Static<typeof RedirectGoCardlessFlowRequestSchema>;

export const StartRedirectGoCardlessFlowResponseSchema = Type.Object({
  redirect_flow_id: Type.String(),
  redirect_url: Type.String(),
});
export type StartRedirectGoCardlessFlowResponseObject = Static<typeof StartRedirectGoCardlessFlowResponseSchema>;

export const CompleteRedirectGoCardlessFlowRequestSchema = Type.Object({
  redirect_flow_id: Type.String(),
  session_token: Type.String(),
});
export type CompleteRedirectGoCardlessFlowRequestObject = Static<typeof CompleteRedirectGoCardlessFlowRequestSchema>;

export const GetPaymentLinkResponseSchema = Type.Object({
  link_payment: Type.String(),
  // UUID du paiement créé — permet au front de proposer le paiement express
  // (carte enregistrée) sans re-parser l'URL de `link_payment`.
  payment_uuid: Type.Optional(Type.String()),
});
export type GetPaymentLinkResponseObject = Static<typeof GetPaymentLinkResponseSchema>;

// ── Paiement express one-time (débit avec le moyen de paiement par défaut) ──

const ExpressPaymentCardSchema = Type.Object({
  brand: Type.String(),
  last4: Type.String(),
});

/**
 * Contexte affiché dans la modale de confirmation avant un débit express. Calculé côté
 * backend : montant **réellement débité** formaté (`amount_to_charge`, pas `amount_all_taxes`
 * qui peut diverger — base produits facturables + chemin d'arrondi distincts) + moyen par
 * défaut : carte (`default_card`, débit immédiat) ou, sans carte par défaut, mandat SEPA
 * (`default_sepa_mandate` : un PRÉLÈVEMENT est lancé, le produit est livré à l'encaissement).
 * `is_chargeable_one_time` est `false` pour un paiement d'abonnement, déjà réglé, ou sans moyen
 * par défaut débitable → le front ouvre alors directement la page de paiement hébergée.
 */
export const ExpressPaymentContextResponseSchema = Type.Object({
  is_chargeable_one_time: Type.Boolean(),
  amount_to_charge_string: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'amount_to_charge' } })),
  default_card: Type.Optional(ExpressPaymentCardSchema),
  default_sepa_mandate: Type.Optional(SepaMandateSummarySchema),
});
export type ExpressPaymentContextResponseObject = Static<typeof ExpressPaymentContextResponseSchema>;

export const PayWithSavedCardRequestSchema = Type.Object({
  payment_uuid: Type.String(),
});
export type PayWithSavedCardRequestObject = Static<typeof PayWithSavedCardRequestSchema>;

export const PayWithSavedCardResponseSchema = Type.Object({
  status: Type.Enum(ExpressPaymentStatus),
  success_url: Type.Optional(Type.String()),
  error_code: Type.Optional(Type.String()),
});
export type PayWithSavedCardResponseObject = Static<typeof PayWithSavedCardResponseSchema>;

// =============================================================================
// Informations de facturation — collectées sur la page de paiement publique
// (scopées par uuid de paiement, le uuid fait office de secret porteur) et
// éditables à tout moment depuis le profil établissement. Requises pour émettre
// une facture B2B conforme (raison sociale, SIRET, adresse de facturation).
// =============================================================================

const paymentBillingInfoFormFields = {
  legal_name: Type.String({
    minLength: 1,
    maxLength: 255,
    title: 'BILLING_INFO_LEGAL_NAME',
    placeholder: 'BILLING_INFO_LEGAL_NAME_PLACEHOLDER',
  }),
  // SIRET & TVA regroupés dans un panneau "Informations complémentaires (optionnel)" :
  // la majorité des pros (coiffeuses, artisans en franchise en base) ne les connaissent
  // pas et n'en ont pas besoin pour une facture simple. Optionnels côté schéma pour ne
  // pas bloquer le paiement (cf. is_complete backend qui n'exige plus le SIRET).
  siret_number: Type.Optional(
    Type.String({
      minLength: 9,
      maxLength: 64,
      title: 'BILLING_INFO_SIRET',
      placeholder: 'BILLING_INFO_SIRET_PLACEHOLDER',
      meta: { group: 'advanced' },
    }),
  ),
  vat_number: Type.Optional(
    Type.String({
      maxLength: 32,
      pattern: intraEuVatNumberPattern,
      title: 'BILLING_INFO_VAT_NUMBER',
      placeholder: 'BILLING_INFO_VAT_NUMBER_PLACEHOLDER',
      meta: { group: 'advanced' },
    }),
  ),
  billing_address: Type.Object(AddressSchema.properties, {
    meta: { input_type: 'address_object' },
    title: 'BILLING_INFO_ADDRESS',
    placeholder: 'BILLING_INFO_ADDRESS',
  }),
};

export const PaymentBillingInfoFormSchema = Type.Object(paymentBillingInfoFormFields);
export type PaymentBillingInfoFormObject = Static<typeof PaymentBillingInfoFormSchema>;

export const PaymentBillingInfoUpsertRequestSchema = Type.Object({
  uuid: Type.String(),
  ...paymentBillingInfoFormFields,
});
export type PaymentBillingInfoUpsertRequestObject = Static<typeof PaymentBillingInfoUpsertRequestSchema>;

export const PaymentBillingInfoResponseSchema = Type.Object({
  legal_name: Type.Optional(Type.String()),
  siret_number: Type.Optional(Type.String()),
  vat_number: Type.Optional(Type.String()),
  billing_address: Type.Optional(Type.Object(AddressSchema.properties)),
  is_complete: Type.Boolean(),
});
export type PaymentBillingInfoResponseObject = Static<typeof PaymentBillingInfoResponseSchema>;
